import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lightbulb, TrendingUp, TrendingDown, Receipt, Target, Coins, Trophy, Package, Users2, BellRing, HeartCrack, FileText, ArrowRight, Loader2, Minus } from "lucide-react";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { money } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const MONTHS = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const mLabel = (p) => { const [y, m] = (p || "").split("-"); return `${MONTHS[parseInt(m, 10)] || m} ${(y || "").slice(2)}`; };
const PERIODS = [{ k: "day", l: "Hoy" }, { k: "week", l: "Semana" }, { k: "month", l: "Mes" }, { k: "year", l: "Año" }];

function Delta({ v, invert }) {
  if (v == null) return <span className="text-xs text-muted-foreground flex items-center gap-1"><Minus className="h-3 w-3" /> s/comparar</span>;
  const good = invert ? v < 0 : v >= 0;
  const Icon = v >= 0 ? TrendingUp : TrendingDown;
  return <span className={`text-xs font-bold flex items-center gap-1 ${good ? "text-green-600" : "text-red-600"}`} data-testid="ci-delta"><Icon className="h-3.5 w-3.5" /> {v > 0 ? "+" : ""}{v}% <span className="font-normal text-muted-foreground">vs. anterior</span></span>;
}

function SummaryCard({ icon: Icon, label, value, delta, accent, invert }) {
  return (
    <Card className="rounded-sm border shadow-none p-4 space-y-1.5" data-testid={`ci-sum-${label}`}>
      <div className="flex items-center gap-2 text-muted-foreground"><Icon className={`h-4 w-4 ${accent}`} /><span className="text-[11px] font-bold uppercase tracking-widest">{label}</span></div>
      <p className="text-2xl font-heading font-black leading-none">{value}</p>
      <Delta v={delta} invert={invert} />
    </Card>
  );
}

function Bars({ rows, labelKey, valueKey, sub, color = "bg-primary", emptyText }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  if (!rows.length) return <p className="text-sm text-muted-foreground py-4 text-center">{emptyText}</p>;
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <div key={i} className="space-y-1" data-testid="ci-bar-row">
          <div className="flex justify-between gap-2 text-xs"><span className="truncate font-medium">{r[labelKey] || "—"}</span><span className="font-mono font-bold shrink-0">{money("GTQ", r[valueKey])}</span></div>
          <div className="h-2.5 bg-muted rounded-sm overflow-hidden"><div className={`h-full ${color} rounded-sm`} style={{ width: `${(r[valueKey] / max) * 100}%` }} /></div>
          {sub && <p className="text-[10px] text-muted-foreground">{sub(r)}</p>}
        </div>
      ))}
    </div>
  );
}

export function CommercialIntelligence() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { can } = useAuth();
  const showVendors = can("sales_ranking:view");

  useEffect(() => {
    setLoading(true);
    api.get(`/intelligence/commercial?period=${period}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [period]);

  const s = data?.summary;
  return (
    <section className="space-y-4" data-testid="commercial-intelligence">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Lightbulb className="h-5 w-5 text-primary" /> Inteligencia Comercial</h3>
        <div className="flex gap-1 bg-muted rounded-sm p-1">
          {PERIODS.map((p) => (
            <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`ci-period-${p.k}`}
              className={`px-3 py-1 text-xs font-bold rounded-sm transition-colors ${period === p.k ? "bg-background shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"}`}>{p.l}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-14"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : !data ? (
        <p className="text-sm text-muted-foreground">No se pudo cargar la información.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard icon={Coins} label="Ventas" value={money("GTQ", s.sales)} delta={s.deltas.sales} accent="text-green-600" />
            <SummaryCard icon={Receipt} label="Facturas" value={s.count} delta={s.deltas.count} accent="text-blue-600" />
            <SummaryCard icon={Target} label="Factura promedio" value={money("GTQ", s.avg_ticket)} delta={s.deltas.avg_ticket} accent="text-purple-600" />
            <SummaryCard icon={TrendingUp} label={s.has_pnl ? `Utilidad bruta (${s.gross_margin}%)` : `Utilidad (${s.margin}%)`} value={money("GTQ", s.has_pnl ? s.gross_profit : s.profit)} delta={s.has_pnl ? s.deltas.gross_profit : s.deltas.profit} accent="text-amber-600" />
          </div>

          {s.has_pnl && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ci-pnl-row">
              <SummaryCard icon={Coins} label="Gastos fijos" value={money("GTQ", s.expenses_fixed)} delta={s.deltas.expenses_fixed} accent="text-orange-600" invert />
              <SummaryCard icon={Coins} label="Gastos variables" value={money("GTQ", s.expenses_variable)} delta={s.deltas.expenses_variable} accent="text-rose-600" invert />
              <SummaryCard icon={Receipt} label="Gastos operativos" value={money("GTQ", s.expenses_total)} delta={s.deltas.expenses_total} accent="text-red-600" invert />
              <SummaryCard icon={Target} label={`Utilidad neta (${s.net_margin}%)`} value={money("GTQ", s.net_profit)} delta={s.deltas.net_profit} accent="text-green-600" />
            </div>
          )}

          {/* Tendencia */}
          <Card className="rounded-sm border shadow-none p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Tendencia de ventas (12 meses)</p>
            <div className="h-56 w-full min-w-0" data-testid="ci-trend-chart">
              <ResponsiveContainer width="100%" height={224} minWidth={0}>
                <AreaChart data={(data.sales_trend || []).map((t) => ({ ...t, label: mLabel(t.month) }))} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs><linearGradient id="ciFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F26A00" stopOpacity={0.35} /><stop offset="95%" stopColor="#F26A00" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => money("GTQ", v)} labelStyle={{ fontWeight: 700 }} contentStyle={{ borderRadius: 4, fontSize: 12 }} />
                  <Area type="monotone" dataKey="sales" name="Ventas" stroke="#F26A00" strokeWidth={2} fill="url(#ciFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Rankings */}
          <div className={`grid grid-cols-1 ${showVendors ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-3`}>
            <Card className="rounded-sm border shadow-none p-4"><p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" /> Top clientes</p>
              <Bars rows={data.top_clients} labelKey="name" valueKey="total" sub={(r) => `${r.count} factura(s)`} emptyText="Sin ventas en el periodo." /></Card>
            <Card className="rounded-sm border shadow-none p-4"><p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><Package className="h-4 w-4 text-blue-500" /> Top productos</p>
              <Bars rows={data.top_products} labelKey="description" valueKey="total" color="bg-blue-500" sub={(r) => `${r.qty} u · ${r.code || ""}`} emptyText="Sin ventas en el periodo." /></Card>
            {showVendors && <Card className="rounded-sm border shadow-none p-4"><p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><Users2 className="h-4 w-4 text-purple-500" /> Vendedores</p>
              <Bars rows={data.vendors} labelKey="vendor" valueKey="sales" color="bg-purple-500" sub={(r) => `${r.count} vta(s) · util. ${money("GTQ", r.profit)}`} emptyText="Sin ventas en el periodo." /></Card>}
          </div>

          {/* Recompra + En riesgo */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card className="rounded-sm border shadow-none p-4" data-testid="ci-repurchase">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><BellRing className="h-4 w-4 text-orange-500" /> Alertas de recompra</p>
              {data.repurchase_alerts.length === 0 ? <p className="text-sm text-muted-foreground py-3">Ningún cliente atrasado en su recompra. 🎉</p> : (
                <div className="space-y-2">
                  {data.repurchase_alerts.map((c, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 rounded-sm border border-border bg-muted/50 px-3 py-2" data-testid={`ci-repurchase-${i}`}>
                      <div className="min-w-0"><p className="text-sm font-medium truncate text-foreground">{c.name}</p>
                        <p className="text-[11px] text-muted-foreground">Compra ~cada {c.avg_days} días · lleva <b className="text-orange-500">{c.days_since}</b> sin comprar</p></div>
                      <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs gap-1 shrink-0" onClick={() => navigate("/cotizaciones")} data-testid={`ci-repurchase-act-${i}`}>Cotizar <ArrowRight className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card className="rounded-sm border shadow-none p-4" data-testid="ci-atrisk">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><HeartCrack className="h-4 w-4 text-red-500" /> Clientes en riesgo</p>
              {data.at_risk_clients.length === 0 ? <p className="text-sm text-muted-foreground py-3">Sin clientes en riesgo de fuga.</p> : (
                <div className="space-y-2">
                  {data.at_risk_clients.map((c, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 rounded-sm border border-border bg-muted/50 px-3 py-2" data-testid={`ci-atrisk-${i}`}>
                      <div className="min-w-0"><p className="text-sm font-medium truncate text-foreground">{c.name}</p>
                        <p className="text-[11px] text-muted-foreground">Sin comprar hace <b className="text-red-500">{c.days_since}</b> días · última {c.last_date}</p></div>
                      <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs gap-1 shrink-0" onClick={() => navigate("/clientes")} data-testid={`ci-atrisk-act-${i}`}>Ver <ArrowRight className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Embudo cotizaciones */}
          <Card className="rounded-sm border shadow-none p-4" data-testid="ci-funnel">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><FileText className="h-4 w-4 text-teal-500" /> Embudo de cotizaciones</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><p className="text-2xl font-heading font-black">{data.quotes_funnel.quoted_count}</p><p className="text-[11px] text-muted-foreground uppercase tracking-wide">Cotizaciones</p><p className="text-xs font-mono text-muted-foreground">{money("GTQ", data.quotes_funnel.quoted_amount)}</p></div>
              <div><p className="text-2xl font-heading font-black text-green-600">{data.quotes_funnel.conversion_rate}%</p><p className="text-[11px] text-muted-foreground uppercase tracking-wide">Conversión</p><p className="text-xs text-muted-foreground">{data.quotes_funnel.converted_count} convertidas</p></div>
              <div><p className="text-2xl font-heading font-black text-amber-600">{data.quotes_funnel.pending_count}</p><p className="text-[11px] text-muted-foreground uppercase tracking-wide">Por cerrar</p><p className="text-xs font-mono text-muted-foreground">{money("GTQ", data.quotes_funnel.pending_amount)}</p></div>
              <div className="flex items-end"><Button variant="outline" className="rounded-sm w-full gap-1 text-xs" onClick={() => navigate("/cotizaciones")} data-testid="ci-funnel-go">Ir a cotizaciones <ArrowRight className="h-3.5 w-3.5" /></Button></div>
            </div>
          </Card>
        </>
      )}
    </section>
  );
}
